import React from 'react';

import i18n from 'util/i18n';

import { RELATIONSHIPS, RELATIONSHIPS_PEOPLE } from 'config/formSections/relationships';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import { findTimelineGaps } from 'helpers/date';
import { validateModel } from 'models/validate';

import { Summary, DateSummary, NameSummary } from 'components/Summary';
import SummaryProgress from 'components/Section/History/SummaryProgress';
import { extractDate } from 'components/Section/History/dateranges';
import { Gap } from 'components/Section/History/Gap';
import { PersonsGap } from 'components/Section/Relationships/People/PersonsGap';
import { sort } from 'components/Section/History/helpers';

import { formConfig } from 'config/forms';
import SvgComponent from 'components/Form/SvgComponent';
import Accordion from 'components/Form/Accordion';
import { ff } from 'helpers/featureFlags';
import { isFormSF852013, isPVQForm } from 'helpers/form';
import Comments from 'components/Form/Comments';
import { getDuplicatePeople } from 'helpers/people';
import ErrorMessage from 'components/ErrorMessage';
import Field from 'components/Form/Field';
import {
  FINANCIAL_BANKRUPTCY,
  FOREIGN_ACTIVITIES_INDIRECT,
  FOREIGN_ACTIVITIES_DIRECT,
  FOREIGN_ACTIVITIES_REAL_ESTATE,
  FOREIGN_ACTIVITIES_SUPPORT,
  FOREIGN_ACTIVITIES_ONGOINGSUPPORT,
  FOREIGN_ACTIVITIES_CURRENT,
      FOREIGN_ACTIVITIES_FUTURE,

  FOREIGN_BUSINESS_ADVICE,
  FOREIGN_BUSINESS_CONTACT,
  FOREIGN_BUSINESS_EMPLOYMENT,
  FOREIGN_BUSINESS_FAMILY,
  FOREIGN_BUSINESS_SPONSORSHIP,
  FOREIGN_BUSINESS_VENTURES,
  FOREIGN_CONTACTS,
  HISTORY_EDUCATION,
  HISTORY_EMPLOYMENT,
  HISTORY_RESIDENCE,
  MILITARY_FOREIGN,
  PSYCHOLOGICAL_DIAGNOSES,
  RELATIONSHIPS_STATUS_MARITAL,
  RELATIONSHIPS_STATUS_COHABITANTS,
  RELATIONSHIPS_RELATIVES,
  SUBSTANCE_USE_ALCOHOL_SUPPLEMENTAL,
  SUBSTANCE_USE_ALCOHOL_ORDERED,
  SUBSTANCE_USE_ALCOHOL_VOLUNTARY,
  SUBSTANCE_USE_ALCOHOL_ADDITIONAL,
  SUBSTANCE_USE_CONTROLLED_ILLEGAL_USAGE,
  SUBSTANCE_USE_DRUGS_ORDERED,
  SUBSTANCE_USE_DRUGS_VOLUNTARY,
} from 'constants/sections';
import Person from './Person';
import PeopleCounter from './PeopleCounter';

const sectionConfig = {
  key: RELATIONSHIPS_PEOPLE.key,
  section: RELATIONSHIPS.name,
  store: RELATIONSHIPS.store,
  subsection: RELATIONSHIPS_PEOPLE.name,
  storeKey: RELATIONSHIPS_PEOPLE.storeKey,
};

export class People extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;

    this.state = {
      uid: `${super.guid()}`,
    };
    this.updateList = this.updateList.bind(this);
    this.peopleSummaryList = this.peopleSummaryList.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      List: this.props.List,
      Comments: this.props.Comments,
      ...queue,
    }, [
      FINANCIAL_BANKRUPTCY,
      FOREIGN_ACTIVITIES_INDIRECT,
      FOREIGN_ACTIVITIES_DIRECT,
      FOREIGN_ACTIVITIES_REAL_ESTATE,
      FOREIGN_ACTIVITIES_SUPPORT,
      FOREIGN_ACTIVITIES_ONGOINGSUPPORT,
      FOREIGN_ACTIVITIES_CURRENT,
      FOREIGN_ACTIVITIES_FUTURE,
      FOREIGN_BUSINESS_ADVICE,
      FOREIGN_BUSINESS_CONTACT,
      FOREIGN_BUSINESS_EMPLOYMENT,
      FOREIGN_BUSINESS_FAMILY,
      FOREIGN_BUSINESS_SPONSORSHIP,
      FOREIGN_BUSINESS_VENTURES,
      FOREIGN_CONTACTS,
      HISTORY_EDUCATION,
      HISTORY_EMPLOYMENT,
      HISTORY_RESIDENCE,
      MILITARY_FOREIGN,
      PSYCHOLOGICAL_DIAGNOSES,
      RELATIONSHIPS_STATUS_MARITAL,
      RELATIONSHIPS_STATUS_COHABITANTS,
      RELATIONSHIPS_RELATIVES,
      SUBSTANCE_USE_ALCOHOL_SUPPLEMENTAL,
      SUBSTANCE_USE_ALCOHOL_ORDERED,
      SUBSTANCE_USE_ALCOHOL_VOLUNTARY,
      SUBSTANCE_USE_ALCOHOL_ADDITIONAL,
      SUBSTANCE_USE_CONTROLLED_ILLEGAL_USAGE,
      SUBSTANCE_USE_DRUGS_ORDERED,
      SUBSTANCE_USE_DRUGS_VOLUNTARY
    ]);
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const date = DateSummary(o.Dates);
    const name = NameSummary(o.Name);
    const name2013 = o.Name2013 ? o.Name2013.value : '';
    const type = i18n.t('relationships.people.person.collection.itemType');

    return Summary({
      type,
      index,
      left: name2013 !== '' ? name2013 : name,
      right: date,
      placeholder: i18n.t('relationships.people.person.collection.summary.unknown'),
    });
  };

  peopleSummaryList = () => this.props.List.items.reduce((dates, item) => {
    // Return if there is no item
    if (!item) { return dates; }

    // Return if the item is not valid
    const { errors } = this.props;
    const itemHasErrors = errors && errors.filter((e) => e.indexOf(item.uuid) > -1).length > 0;
    if (itemHasErrors) { return dates; }

    const knownDates = item.Item && item.Item.Dates;
    if (!knownDates) { return dates; }

    const kfrom = extractDate(knownDates.from);
    const kto = extractDate(knownDates.to);
    const present = (knownDates || {}).present || false;
    if (kfrom && (present || kto)) {
      return dates.concat(item.Item.Dates);
    }
    return dates;
  }, []);

  render() {
    const {
      formType, formVersion, errors, List, formErrors, features
    } = this.props;

    const years = formType && formConfig[formType] && formConfig[formType].RELATIONSHIPS_PEOPLE_YEARS;

    const totalPersons = 3; // 3 people are always required for this section, regardless of formType

    const { items, branch } = List;

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);

    // Deprecated with EN-4733 & EN-5264
    const dateRanges = items
      .filter(
        (i) => !!(i?.Item
          && i.Item.Dates
          && validateModel({ Dates: i.Item.Dates }, { Dates: { presence: true, daterange: true } }))
      )
      .map((i) => i?.Item?.Dates);

    // Deprecated with EN-4733 & EN-5264
    const gaps = findTimelineGaps({ years }, dateRanges);

    // Deprecated with EN-4733 & EN-5264
    const showGapError = !ff(features).getActive('EN-4733')
      ? branch
      && branch.value === 'No'
      && gaps.length > 0
      && formErrors
      && formErrors.filter((e) => e.code === 'gap.relationshipsPeople')
      : false;

    // Deprecated with EN-4733 & EN-5264
    const gapError = (
      <Gap
        title={i18n.t('relationships.people.person.gap.title')}
        para={i18n.t('relationships.people.person.gap.para', { years })}
        gaps={gaps}
      />
    );

    // Number of list items with no errors
    const validCount = List.items.filter(
      (i) => i && i.Item && errors.filter((e) => e.indexOf(i.uuid) > -1).length <= 0
    ).length;

    // Deprecated with EN-4733 & EN-5264
    const showPersonsGapError = !ff(features).getActive('EN-4733')
      ? validCount < totalPersons
      && branch
      && branch.value === 'No'
      && formErrors.filter((e) => e.code === 'error.list.length.relationshipsPeople')
      : false;

    // Deprecated with EN-4733 & EN-5264
    const personsGapError = (
      <div className="field accordionfield" data-uuid={this.state.uid}>
        <span id={this.state.uid} className="anchor" aria-hidden="true" />
        <h4 className="title h4 hidden">
          Provide three people who know you well and who preferably live in the U.S.
        </h4>
        <PersonsGap
          title={i18n.t('relationships.people.person.personsGap.title')}
          message={i18n.t('relationships.people.person.personsGap.message')}
        />
      </div>
    );

    const duplicatePeopleWhoKnowYou = getDuplicatePeople(List?.items);

    const duplicatePeopleWhoKnowYouError = (errorsList) => (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('error.list.duplicate.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (errorsList) => (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...errorsList.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    const isPvq = isPVQForm(formType);
    return isPvq ? (
      <div
        className="section-content people"
        data-section={RELATIONSHIPS.key}
        data-subsection={RELATIONSHIPS_PEOPLE.key}
      >
        <h1 className="section-header">{i18n.t('relationships.pvq.people.sectionTitle.title')}</h1>

        {i18n.m('relationships.pvq.people.para.intro', { years })}

        <span id="scrollToPeople" />
        <div className="summary-progress">
          <div className="summaryprogress progress">
            <SummaryProgress
              className="people-summary"
              List={this.peopleSummaryList} // eslint-disable-line @typescript-eslint/unbound-method
              title={i18n.t('relationships.pvq.people.summaryProgress.title', { years })}
              unit={i18n.t('relationships.pvq.people.summaryProgress.unit')}
              total={years}
            >
              <div className="summary-icon">
                <SvgComponent
                  src="/img/people-who-know-you.svg"
                  alt={i18n.t('relationships.pvq.people.summaryProgress.svgAlt', { years })}
                />
              </div>
            </SummaryProgress>
          </div>
          <div className="summaryprogress counter">
            <PeopleCounter minimum={3} validCount={validCount} />
          </div>
        </div>

        <Accordion
          {...this.props.List}
          defaultState={this.props.defaultState}
          scrollToBottom={this.props.scrollToBottom}
          realtime
          sort={sort}
          summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
          errors={accordionErrors}
          onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          description={i18n.t('relationships.pvq.people.person.collection.description')}
          appendTitle={i18n.t('relationships.pvq.people.person.collection.appendTitle')}
          appendLabel={i18n.t('relationships.pvq.people.person.collection.appendLabel')}
          additionalErrors={
            (showGapError && gapError)
            || (
              duplicatePeopleWhoKnowYou.errors.length > 0
              && duplicatePeopleWhoKnowYouError(duplicatePeopleWhoKnowYou)
            )
            || (
              duplicatePeopleWhoKnowYou.warnings.length > 0
              && duplicatePeopleWhoKnowYouWarning(duplicatePeopleWhoKnowYou)
            )
          }
          personsGapError={showPersonsGapError && personsGapError}
          minItems={3}
          ignoreInitialScrollTo
        >
          <Person
            name="Item"
            bind
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            formType={formType}
            formVersion={formVersion}
          />
        </Accordion>
      </div>
    ) : (
      <div
        className="section-content people"
        data-section={RELATIONSHIPS.key}
        data-subsection={RELATIONSHIPS_PEOPLE.key}
      >
        <h1 className="section-header">{i18n.t('relationships.people.sectionTitle.title')}</h1>

        {i18n.m('relationships.people.para.intro', { years })}

        <span id="scrollToPeople" />
        <div className="summary-progress">
          <div className="summaryprogress progress">
            <SummaryProgress
              className="people-summary"
              List={this.peopleSummaryList} // eslint-disable-line @typescript-eslint/unbound-method
              title={i18n.t('relationships.people.summaryProgress.title', { years })}
              unit={i18n.t('relationships.people.summaryProgress.unit')}
              total={years}
            >
              <div className="summary-icon">
                <SvgComponent
                  src="/img/people-who-know-you.svg"
                  alt={i18n.t('relationships.people.summaryProgress.svgAlt', { years })}
                />
              </div>
            </SummaryProgress>
          </div>
          <div className="summaryprogress counter">
            <PeopleCounter minimum={3} validCount={validCount} />
          </div>
        </div>

        <Accordion
          {...this.props.List}
          defaultState={this.props.defaultState}
          scrollToBottom={this.props.scrollToBottom}
          realtime
          sort={sort}
          summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
          errors={accordionErrors}
          onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          description={i18n.t('relationships.people.person.collection.description')}
          appendTitle={i18n.t('relationships.people.person.collection.appendTitle')}
          appendLabel={i18n.t('relationships.people.person.collection.appendLabel')}
          additionalErrors={
            (showGapError && gapError)
            || (
              duplicatePeopleWhoKnowYou.errors.length > 0
              && duplicatePeopleWhoKnowYouError(duplicatePeopleWhoKnowYou)
            )
            || (
              duplicatePeopleWhoKnowYou.warnings.length > 0
              && duplicatePeopleWhoKnowYouWarning(duplicatePeopleWhoKnowYou)
            )
          }
          personsGapError={showPersonsGapError && personsGapError}
          minItems={3}
          ignoreInitialScrollTo
        >
          <Person
            name="Item"
            bind
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            formType={formType}
            formVersion={formVersion}
          />
        </Accordion>
        {!isFormSF852013(formType, formVersion) && (
          <Comments
            title={i18n.t('relationships.comments.optional')}
            name="PeopleComments"
            className="optional-comment"
            attribute="PeopleComments"
            {...this.props.Comments}
            onUpdate={this.updateComments}
            required={false}
          />
        )}
      </div>
    );
  }
}

People.defaultProps = {
  List: { items: [] },
  Comments: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  addressBooks: {},
  dispatch: () => { },
  defaultState: true,
  scrollToBottom: '.bottom-btns',
  scrollIntoView: false,
  errors: [],
};

export default connectSubsection(People, sectionConfig);
